import { ChevronDown } from "lucide-react";

import { ErrorAlert, Form, cx, Modal, Field, Input, Button } from "@/components/ui";
import { DNS_TYPES } from "@/lib/domains";
import { useUnsavedChanges } from "@/lib/unsavedChanges";

import {
  TTL_OPTIONS,
  VALUE_PLACEHOLDER,
  fieldError,
  hasFieldError,
  isDraftEdited,
  ttlLabel,
  usesPriority,
  type RecordDraft,
} from "./util";
import { Hint } from "@/components/domains/parts";

/** Figma 4086-1246 "Add DNS record" (and its edit twin). Submitting asks for a preview first. */
export function RecordModal({
  open,
  draft,
  onChange,
  onClose,
  onSubmit,
  onDelete,
  submitting,
  error,
  siteName,
}: {
  open: boolean;
  draft: RecordDraft;
  onChange: (draft: RecordDraft) => void;
  onClose: () => void;
  onSubmit: () => void;
  onDelete: () => void;
  submitting: boolean;
  error: unknown;
  siteName: string | null;
}) {
  const editing = draft.record !== null;
  const set = (patch: Partial<RecordDraft>) => onChange({ ...draft, ...patch });
  const ttls = TTL_OPTIONS.some((o) => o.value === draft.ttl)
    ? TTL_OPTIONS
    : [...TTL_OPTIONS, { value: draft.ttl, label: ttlLabel(draft.ttl) }].sort((a, b) => a.value - b.value);
  const fieldsWithErrors = ["type", "name", "content", "ttl", "priority"];
  // The draft lives in the parent and outlasts this dialog (the review step has its own guard in the DNS tab),
  // so only an open dialog whose draft was edited has anything to lose here.
  const unsaved = useUnsavedChanges(open && isDraftEdited(draft) && !submitting, { ignoreSearch: true });
  const close = () => unsaved.guard(onClose);

  return (
    <>
      <Modal
        open={open}
        onClose={close}
        title={editing ? "Edit DNS record" : "Add DNS record"}
        subtitle={
          editing
            ? "Advanced — most people never need to change records manually"
            : "Advanced — most people never need to add records manually"
        }
        note="You'll see the impact before it's applied."
        footer={
          <>
            {editing && (
              <Button
                variant="ghost"
                size="lg"
                className="text-[#b52522] hover:text-[#b52522]"
                onClick={onDelete}
              >
                Delete record
              </Button>
            )}
            <Button variant="secondary" size="lg" onClick={close}>
              Cancel
            </Button>
            <Button variant="brand" size="lg" type="submit" form="dns-record-form" loading={submitting}>
              {editing ? "Review change" : "Add record"}
            </Button>
          </>
        }
      >
        <Form
          id="dns-record-form"
          className="flex flex-col gap-4"
          error={error}
          onSubmit={(e) => {
            e.preventDefault();
            if (draft.content.trim()) onSubmit();
          }}
        >
          {draft.record?.managed_by === "website" && (
            <Hint tone="warning" title="CortexGrip set this record up for your website">
              It connects this domain to {siteName ?? "your website"}. Changing it can stop visitors reaching
              your site.
            </Hint>
          )}
          <div className="flex flex-col gap-2">
            <p className="text-[13px] font-semibold text-[#5c5551]" id="dns-type-label">
              Record type
            </p>
            <div role="radiogroup" aria-labelledby="dns-type-label" className="flex flex-wrap gap-2">
              {DNS_TYPES.map((t) => (
                <Button
                  unstyled
                  key={t}
                  type="button"
                  role="radio"
                  aria-checked={draft.type === t}
                  onClick={() =>
                    set({
                      type: t,
                      priority: usesPriority(t) && draft.priority === "" ? "10" : draft.priority,
                    })
                  }
                  className={cx(
                    "min-w-[42px] rounded-lg px-3.5 py-2 text-[13px] font-medium",
                    draft.type === t
                      ? "bg-[#221c19] text-white"
                      : "border border-border bg-surface text-fg hover:bg-muted",
                  )}
                >
                  {t}
                </Button>
              ))}
            </div>
            {fieldError(error, "type") && (
              <p className="text-xs text-[#b52522]">{fieldError(error, "type")}</p>
            )}
          </div>
          <Field size="lg" label="Host" name="name">
            {(control) => (
              <Input
                size="lg"
                {...control}
                value={draft.name}
                autoComplete="off"
                spellCheck={false}
                placeholder={
                  draft.type === "SRV" ? "_service._tcp (e.g. _sip._tcp)" : "@ or subdomain (e.g. www, mail)"
                }
                onChange={(e) => set({ name: e.target.value })}
              />
            )}
          </Field>
          <Field size="lg" label="Value" name="content" required>
            {(control) => (
              <Input
                size="lg"
                {...control}
                value={draft.content}
                autoComplete="off"
                spellCheck={false}
                placeholder={VALUE_PLACEHOLDER[draft.type]}
                onChange={(e) => set({ content: e.target.value })}
              />
            )}
          </Field>
          {usesPriority(draft.type) && (
            <Field size="lg" label="Priority" name="priority" hint="Lower numbers are tried first">
              {(control) => (
                <Input
                  size="lg"
                  {...control}
                  inputMode="numeric"
                  value={draft.priority}
                  placeholder="10"
                  onChange={(e) => set({ priority: e.target.value.replace(/\D/g, "").slice(0, 5) })}
                />
              )}
            </Field>
          )}
          <Field size="lg" label="TTL" name="ttl">
            {(control) => (
              <div className="relative flex items-center rounded-[10px] bg-[#fef4e5] focus-within:ring-2 focus-within:ring-brand-500">
                <select
                  {...control}
                  value={draft.ttl}
                  onChange={(e) => set({ ttl: Number(e.target.value) })}
                  className="w-full appearance-none bg-transparent p-3 pr-8 text-sm font-medium text-warning-fg outline-none"
                >
                  {ttls.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.value}
                    </option>
                  ))}
                </select>
                <span className="pointer-events-none absolute right-8 text-xs text-[#766e6a]">
                  seconds ({ttlLabel(draft.ttl)}){draft.ttl === 3600 ? " - recommended default" : ""}
                </span>
                <ChevronDown
                  aria-hidden
                  className="pointer-events-none absolute right-3 size-3.5 text-[#e28e00]"
                />
              </div>
            )}
          </Field>
          <Hint tone="success" title="Changes here can affect your live website or email">
            If you're not sure what this does, check with whoever manages your website before adding a custom
            record.
          </Hint>
          {Boolean(error) && !hasFieldError(error, fieldsWithErrors) && <ErrorAlert error={error} />}
          {/* Hidden Enter-key submit trigger, not a visible action — not the shared Button. */}
          {/* eslint-disable-next-line portal/no-raw-button */}
          <button type="submit" hidden aria-hidden tabIndex={-1} />
        </Form>
      </Modal>
      {unsaved.modal}
    </>
  );
}
